import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Upload, Loader2, Trash2 } from "lucide-react";
import { api, errText } from "@/lib/api";
import { fmtMoney, today } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useCustomer } from "@/lib/customer";

const HEADER_FIELDS = [
  ["poNumber", "PO number", "text"],
  ["poDate", "PO date", "date"],
  ["poProcessedDate", "PO processed date", "date"],
  ["soNumber", "SO number", "text"],
  ["branchCode", "Branch code", "text"],
  ["branchName", "Branch name", "text"],
  ["city", "City", "text"],
  ["state", "State", "text"],
  ["spocName", "SPOC name", "text"],
  ["spocContact", "SPOC contact", "text"],
];

const emptyLine = (itemType = "") => ({ itemType, product: "", qty: "", rate: "" });

export default function NewPo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [header, setHeader] = useState({ poDate: today() });
  const [address, setAddress] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [itemTypes, setItemTypes] = useState([]);
  const [people, setPeople] = useState([]);
  const [owner, setOwner] = useState("");
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [fromPdf, setFromPdf] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const { customerId } = useCustomer();

  // a PO number already on this customer is a warning, never a block
  useEffect(() => {
    const number = (header.poNumber || "").trim();
    if (!number) {
      setDuplicate(null);
      return;
    }
    const timer = setTimeout(() => {
      api
        .get("/po-duplicate", { params: { poNumber: number, branchCode: header.branchCode || "" } })
        .then(({ data }) => setDuplicate(data.exists ? data : null))
        .catch(() => setDuplicate(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [header.poNumber, header.branchCode, customerId]);

  useEffect(() => {
    api
      .get("/meta")
      .then(({ data }) => {
        setItemTypes(data.itemTypes || []);
        setPeople(data.people || []);
        const first = data.itemTypes?.[0]?.name;
        if (first) {
          setLines((ls) =>
            ls.map((l, i) => (i === 0 && !l.itemType && !l.product ? { ...l, itemType: first } : l))
          );
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.name) setOwner((o) => o || user.name);
  }, [user]);

  const setH = (k, v) => setHeader((h) => ({ ...h, [k]: v }));
  const setLine = (i, patch) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const total = lines.reduce(
    (sum, l) => sum + (Number(l.qty) || 0) * (Number(l.rate) || 0),
    0
  );

  const save = async () => {
    if (!header.poNumber || !header.poDate) {
      toast.error("PO number and PO date are needed");
      return;
    }
    const usable = lines.filter((l) => l.itemType || l.product || l.qty);
    if (usable.length === 0) {
      toast.error("Add at least one line");
      return;
    }
    setBusy(true);
    try {
      const payload = usable.map((l) => ({
        ...header,
        branchAddress: address || null,
        itemType: l.itemType || null,
        product: l.product || null,
        qty: l.qty === "" ? null : Number(l.qty),
        rate: l.rate === "" ? null : Number(l.rate),
        owner: owner || null,
      }));
      const { data } = await api.post("/polines", { lines: payload });
      toast.success(`PO saved with ${data.count} line${data.count === 1 ? "" : "s"}`);
      navigate(`/po-history/${data.created[0].id}`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setExtracting(true);
    try {
      const { data } = await api.post("/po-extract", form);
      const e = data.extracted;
      setHeader({
        poNumber: e.poNumber || "",
        poDate: e.poDate || "",
        poProcessedDate: "",
        soNumber: "",
        branchCode: e.branchCode || "",
        branchName: e.branchName || "",
        city: e.city || "",
        state: e.state || "",
        spocName: e.spocName || "",
        spocContact: e.spocContact || "",
      });
      setAddress(e.branchAddress || "");
      setLines(
        (e.lines || []).map((l) => ({
          itemType: l.itemType || "",
          product: l.product || "",
          qty: l.qty ?? "",
          rate: l.rate ?? "",
        }))
      );
      setFromPdf(true);
      toast.success("Read from the PDF. Check every field, then save.");
    } catch (err) {
      toast.error(errText(err));
      toast.info("Enter the PO by hand instead");
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="new-po-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">New PO</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Fill the header once, add a line for each item, then save the whole PO in one go.
        </p>
      </div>

      <div className="panel p-3 sm:p-4">
        <h2 className="text-sm font-medium">Upload the PO PDF</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          The app reads the PDF and fills this form for you to check. Nothing is saved until you press save.
        </p>
        <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-md border border-input px-3 py-2 text-sm hover:bg-secondary">
          {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {extracting ? "Reading the PDF…" : "Choose a PDF"}
          <input
            type="file"
            accept=".pdf"
            className="hidden"
            data-testid="po-pdf-input"
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </label>
      </div>

      {fromPdf && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300" data-testid="extracted-notice">
          These details came from the PDF and nothing is saved yet. Check every field and correct anything the
          reader got wrong.
        </div>
      )}

      <div className="panel p-3 sm:p-4">
        <h2 className="text-sm font-medium">PO header</h2>
        {duplicate && (
          <div
            className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
            data-testid="duplicate-po-warning"
          >
            <span>
              {duplicate.poNumber} is already on the system for {duplicate.branchName || "this branch"}
              {duplicate.branchCode ? ` (${duplicate.branchCode})` : ""} with {duplicate.lineCount} line
              {duplicate.lineCount === 1 ? "" : "s"}. You can still save this one if you mean to.
            </span>
            <button
              className="font-medium underline"
              onClick={() => navigate(`/po-history/${duplicate.lineId}`)}
              data-testid="duplicate-po-open-button"
            >
              Open the existing PO
            </button>
          </div>
        )}
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {HEADER_FIELDS.map(([key, label, type]) => (
            <div key={key}>
              <Label className="field-label">{label}</Label>
              <Input
                type={type}
                value={header[key] || ""}
                onChange={(e) => setH(key, e.target.value)}
                className={type === "date" ? "font-mono" : ""}
                data-testid={`new-po-${key}-input`}
              />
            </div>
          ))}
          <div className="sm:col-span-2 lg:col-span-3">
            <Label className="field-label">Branch address</Label>
            <Textarea
              value={address}
              rows={2}
              onChange={(e) => setAddress(e.target.value)}
              data-testid="new-po-branchAddress-input"
            />
          </div>
          <div>
            <Label className="field-label">Owner</Label>
            <select
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              data-testid="new-po-owner-select"
            >
              <option value="">Unassigned</option>
              {people.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="panel p-3 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium">
            Lines — one for each item on the PO
          </h2>
          <span className="text-xs text-muted-foreground">
            {lines.length} line{lines.length === 1 ? "" : "s"} · {fmtMoney(total)}
          </span>
        </div>

        <div className="mt-3 space-y-3">
          {lines.map((l, i) => (
            <div key={i} className="rounded-md border border-border p-3" data-testid={`new-po-line-${i}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Line {i + 1}</span>
                {lines.length > 1 && (
                  <button
                    onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                    className="text-muted-foreground hover:text-red-600"
                    data-testid={`new-po-line-remove-${i}`}
                    aria-label="Remove this line"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="sm:col-span-2 lg:col-span-4">
                  <Label className="field-label">Product — copy it from the PO exactly as written</Label>
                  <Textarea
                    value={l.product}
                    rows={2}
                    onChange={(e) => setLine(i, { product: e.target.value })}
                    data-testid={`new-po-line-${i}-product-input`}
                  />
                </div>
                <div>
                  <Label className="field-label">Item type</Label>
                  <select
                    value={l.itemType}
                    onChange={(e) => setLine(i, { itemType: e.target.value })}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    data-testid={`new-po-line-${i}-itemType-select`}
                  >
                    <option value="">Pick a type</option>
                    {itemTypes.map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.name}
                      </option>
                    ))}
                    {l.itemType && !itemTypes.some((t) => t.name === l.itemType) && (
                      <option value={l.itemType}>{l.itemType}</option>
                    )}
                  </select>
                </div>
                <div>
                  <Label className="field-label">Qty</Label>
                  <Input
                    type="number"
                    value={l.qty}
                    onChange={(e) => setLine(i, { qty: e.target.value })}
                    className="font-mono"
                    data-testid={`new-po-line-${i}-qty-input`}
                  />
                </div>
                <div>
                  <Label className="field-label">Rate</Label>
                  <Input
                    type="number"
                    value={l.rate}
                    onChange={(e) => setLine(i, { rate: e.target.value })}
                    className="font-mono"
                    data-testid={`new-po-line-${i}-rate-input`}
                  />
                </div>
                <div>
                  <Label className="field-label">Line value</Label>
                  <div className="flex h-9 items-center font-mono text-sm" data-testid={`new-po-line-${i}-value`}>
                    {fmtMoney((Number(l.qty) || 0) * (Number(l.rate) || 0))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => setLines((ls) => [...ls, emptyLine(itemTypes[0]?.name || "FE")])}
          data-testid="new-po-add-line-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add a line
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={busy} data-testid="new-po-save-button">
          {busy ? "Saving…" : `Save the PO (${lines.length} line${lines.length === 1 ? "" : "s"})`}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setHeader({ poDate: today() });
            setAddress("");
            setLines([emptyLine(itemTypes[0]?.name || "FE")]);
            setFromPdf(false);
          }}
          data-testid="new-po-clear-button"
        >
          Clear the form
        </Button>
      </div>
    </div>
  );
}
